// src/pages/ConversationView.js
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import MessageList from "../components/conversation/MessageList";
import MessageInput from "../components/conversation/MessageInput";
import StickyInputBar from "../components/conversation/StickyInputBar";
import VideoPlayerModal from "../components/conversation/VideoPlayerModal";
import { authFetch } from '../utils/auth';

// Copy for the two bounded-cost rejections the backend can answer a send
// with (routes/conversations.js): 429 rate_limited and 400 message_too_long.
// Both are circuit breakers a real user should never reach, so the tone is
// deliberately calm — no blame, just "wait" / "shorten".
const RATE_LIMIT_NOTICE = "Du sender meldinger litt raskt. Vent et øyeblikk og prøv igjen.";
const TOO_LONG_NOTICE = "Meldingen er for lang. Prøv å korte den ned litt.";

// Every other failed send (network error, 5xx): same calm tone. The text is
// handed back to the input, so trying again needs no retyping.
const SEND_FAILED_NOTICE = "Svaret kom ikke frem. Det er ikke noe du har gjort feil – prøv å sende igjen om et lite øyeblikk.";

// authFetch throws on non-OK with `status` set and the response body text in
// `message`, so the 400 branch has to sniff the error code from the text.
const noticeForSendError = (err) => {
  if (err?.status === 429) return RATE_LIMIT_NOTICE;
  if (err?.status === 400 && /message_too_long/.test(err.message || "")) return TOO_LONG_NOTICE;
  return SEND_FAILED_NOTICE;
};

// Answers being generated, by conversation id: { content, result }, where
// result resolves to { conversation } or { error } and never rejects. Module
// scope, so a pending answer belongs to its conversation, not to a page
// instance: switching to another conversation (same instance, new :id) or
// leaving and coming back (remount) never shows the loading state in the
// wrong conversation, and the original one picks its answer up again.
const answersInFlight = new Map();

const requestAnswer = (conversationId, content) => {
  const result = authFetch(`/conversations/${conversationId}/messages/ai`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content }),
  })
    .then((response) => response.json())
    .then((data) => ({ conversation: data.conversation || null }))
    .catch((error) => ({ error }));
  const entry = { content, result };
  answersInFlight.set(conversationId, entry);
  result.then(() => {
    if (answersInFlight.get(conversationId) === entry) answersInFlight.delete(conversationId);
  });
  return entry;
};

const ConversationView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Keep useMsal hook for auth context without destructuring
  useMsal();
  const [conversation, setConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  // The conversation whose answer this view is waiting for. `loading` is
  // derived from it, so the loading state only shows in that conversation.
  const [awaitingAnswerFor, setAwaitingAnswerFor] = useState(null);
  const loading = awaitingAnswerFor === id;
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  // Inline, dismissable notice above the input (see noticeForSendError).
  // Cleared on the next send attempt so a stale notice never lingers.
  const [notice, setNotice] = useState(null);
  const messagesColumnRef = useRef(null);
  // True while the user sits at (or near) the bottom of the scroll container.
  // Late content growth (image loads, font swap) re-lands only while pinned,
  // so a reader who scrolled up is never yanked back down.
  const pinnedToBottomRef = useRef(true);
  // Which conversation id has had its initial (instant) landing.
  const landedConversationRef = useRef(null);
  // Which conversation id the router-state initialMessage (NewConversation's
  // redirect) has been sent for. A ref, not state: marking the send must not
  // re-run the fetch effect (that re-showed the skeleton after the first
  // answer), and effect re-runs can never send it twice.
  const initialMessageSentForRef = useRef(null);
  // The conversation on screen, for continuations that outlive a navigation.
  const currentIdRef = useRef(id);
  currentIdRef.current = id;

  // Land at the absolute bottom of the scroll container. Scrolling the
  // container to scrollHeight (UA-clamped to the max) is the only target that
  // accounts for the sticky input block's flow space — scrollIntoView on an
  // end-of-messages sentinel aligns to the scrollport edge instead, which
  // stops one sticky-block-height (~1 message) short of the bottom. This is
  // also the same geometry (scrollHeight/scrollTop/clientHeight) the
  // pinned-to-bottom tracking reads, so the two can never disagree.
  const scrollToBottom = useCallback((behavior = "smooth") => {
    const container = document.getElementById("main-scroll-container");
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior });
    }
  }, []);

  // Show the loading state in `conversationId` until its answer is in, then
  // apply the answer — only while that conversation is still on screen. A
  // failure is never silent: the notice for that error, the unsaved
  // optimistic message removed and the text handed back to the input. (A
  // failed first message also stays in the conversation: the backend stored
  // it on create.)
  const awaitAnswer = useCallback(async (conversationId, { content, result }) => {
    setAwaitingAnswerFor(conversationId);
    const { conversation: updated, error: sendError } = await result;
    setAwaitingAnswerFor((current) => (current === conversationId ? null : current));
    if (currentIdRef.current !== conversationId) return;

    if (updated) {
      // The POST response carries the whole conversation; no re-fetch, so an
      // optimistic message can't disappear in a race.
      setConversation(updated);
    } else if (sendError) {
      console.error('[ConversationView] Failed to get an answer:', sendError);
      setNotice(noticeForSendError(sendError));
      setNewMessage(content);
      setConversation((prev) => prev && {
        ...prev,
        messages: prev.messages.filter((m) => !m.isOptimistic),
      });
    }
  }, []);

  // Send `content` in the conversation on screen (input or question CTA).
  const sendMessage = useCallback((content) => {
    setNotice(null);
    setConversation((prev) => {
      // Retrying the question already at the end of the conversation (a
      // failed first message) doesn't show it twice; the backend doesn't
      // store it twice either (routes/conversations.js isDuplicate).
      const last = prev.messages[prev.messages.length - 1];
      if (last?.sender === 'user' && last.content === content) return prev;
      // Optimistic UI: add the user message immediately
      const optimisticMessage = {
        sender: 'user',
        content,
        timestamp: new Date(),
        _id: `temp-${Date.now()}`, // Temporary ID
        isOptimistic: true // Flag for identification
      };
      return { ...prev, messages: [...prev.messages, optimisticMessage] };
    });
    awaitAnswer(id, requestAnswer(id, content));
  }, [id, awaitAnswer]);

  useEffect(() => {
    // A newer run (id change, StrictMode re-mount) owns the page state.
    let cancelled = false;
    const fetchConversation = async () => {
      setPageLoading(true);
      setError(null);
      // An answer still being generated here (sent before the user switched
      // away). Read before the fetch, so it is picked up even if it lands
      // while the conversation loads.
      const pendingAnswer = answersInFlight.get(id);
      try {
        const response = await authFetch(`/conversations/${id}`);
        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();
          if (cancelled) return;

          const initialMessage = location.state?.initialMessage;
          const sendInitial = Boolean(initialMessage) && !pendingAnswer && initialMessageSentForRef.current !== id;
          if (sendInitial) {
            initialMessageSentForRef.current = id;
            // Clear the router state before sending, so a reload during the
            // wait can't send the first message again.
            window.history.replaceState({}, document.title);
          }
          // One commit replaces the skeleton with the conversation (the
          // backend stored the user's message on create) and, for a new
          // conversation or a pending answer, the loading header under it —
          // the answer is awaited in the real view, not behind the skeleton.
          setConversation(data);
          setPageLoading(false);
          const answer = sendInitial ? requestAnswer(id, initialMessage) : pendingAnswer;
          if (answer) awaitAnswer(id, answer);
        } else {
          if (response.status === 404) {
            setError('Samtalen ble ikke funnet.');
          } else {
            setError('Kunne ikke hente samtalen. Vennligst prøv igjen senere.');
          }
          console.error('Failed to fetch conversation:', response.status);
        }
      } catch (err) {
        setError('En feil oppstod under henting av samtalen.');
        console.error('Error fetching conversation:', err);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };

    fetchConversation();
    return () => {
      cancelled = true;
    };
  }, [id, location.state, awaitAnswer]);

  // Land at the newest message. The first render of a loaded conversation gets
  // an instant pre-paint land (useLayoutEffect + behavior 'auto') — a smooth
  // scroll issued while the message list is still laying out gets cut short on
  // mobile browsers. Later updates (messages sent/received) scroll smoothly.
  useLayoutEffect(() => {
    // While the skeleton shows, the messages aren't in the DOM — wait for
    // the real UI so the one-shot initial landing isn't consumed against the
    // skeleton's geometry.
    if (pageLoading) return;
    if (!conversation?.messages?.length) return;
    // Ignore stale data from the previous conversation while a new id loads
    if (conversation._id && conversation._id !== id) return;
    if (landedConversationRef.current !== id) {
      landedConversationRef.current = id;
      pinnedToBottomRef.current = true;
      scrollToBottom("auto");
    } else {
      scrollToBottom("smooth");
    }
  }, [conversation, id, pageLoading, scrollToBottom]);

  // Track whether the user is at the bottom of the scroll container.
  useEffect(() => {
    const container = document.getElementById("main-scroll-container");
    if (!container) return;
    const handleScroll = () => {
      const distance =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      pinnedToBottomRef.current = distance < 100;
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Content can grow after the initial land (WelcomeVideoThumbnail image,
  // font swap, expanded citations). While pinned to the bottom, follow the
  // growth with an instant re-land instead of relying on a timing delay.
  useEffect(() => {
    if (!conversation || pageLoading) return;
    const content = messagesColumnRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (pinnedToBottomRef.current) scrollToBottom("auto");
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [conversation, pageLoading, scrollToBottom]);

  // Listen for question CTA auto-submit event
  useEffect(() => {
    const handleQuestionSubmit = (event) => {
      const { query, conversationId: targetId } = event.detail;

      // Only handle events for this conversation
      if (targetId !== id) return;

      const userMessage = query.trim();
      if (!userMessage) return;
      sendMessage(userMessage);
    };

    window.addEventListener('submit-question', handleQuestionSubmit);
    return () => window.removeEventListener('submit-question', handleQuestionSubmit);
  }, [id, sendMessage]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (loading || !newMessage.trim()) return;

    const userMessage = newMessage.trim();
    setNewMessage(""); // Clear input immediately
    sendMessage(userMessage);
  };

  // Render loading skeleton — same column and message geometry as the real
  // view: Coord's 24 px avatar + name header above its answer (MessageList's
  // CoordMessageHeader), the user's bubble right-aligned with no avatar.
  if (pageLoading) {
    return (
      <div className="bg-background min-h-full">
        <div className="p-6 pb-0 max-w-4xl mx-auto">
          {/* Title skeleton */}
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-8 animate-pulse"></div>

          {/* Messages skeleton */}
          <div className="space-y-6 mb-6">
            <div>
              <div className="flex items-center gap-2 h-6 mb-2">
                <div className="h-6 w-6 rounded-full bg-gray-300 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-14 animate-pulse"></div>
              </div>
              <div className="h-20 bg-gray-200 rounded w-full animate-pulse"></div>
            </div>
            <div className="flex justify-end">
              <div className="h-16 bg-gray-200 rounded-xl w-[70%] animate-pulse"></div>
            </div>
          </div>
        </div>
        
        {/* Input skeleton */}
        <div className="p-6 max-w-4xl mx-auto">
          <div className="bg-background-card rounded-2xl p-4 border border-border-light">
            <div className="h-12 bg-gray-200 rounded-lg w-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="bg-background min-h-full">
        <div className="p-6 max-w-3xl mx-auto">
          <div className="bg-error-light border border-error rounded-xl text-error-dark px-6 py-4 relative mb-6 animate-fade-in">
            <strong className="font-bold">Feil: </strong>
            <span className="block sm:inline">{error}</span>
            <button 
              className="mt-4 bg-error hover:bg-error-dark text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 hover:shadow-md active:transform active:scale-95"
              onClick={() => navigate('/conversations')}
            >
              Tilbake til samtaler
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render conversation when data is loaded
  if (!conversation?.messages) {
    return (
      <div className="bg-background min-h-full">
        <div className="p-6 max-w-3xl mx-auto">
          <div className="bg-warning-50 border border-warning-200 text-warning-700 px-4 py-3 rounded relative">
            <strong className="font-bold">Merknad: </strong>
            <span className="block sm:inline">Samtalen ble funnet, men inneholder ingen meldinger.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    // Column filling the scroll container (#main-scroll-container). The negative
    // bottom margin cancels the shell's p-4/md:p-8 bottom padding so the sticky
    // input's natural end-of-scroll position coincides with its stuck position —
    // otherwise the input would shift down by the padding amount at max scroll.
    // overflow-anchor is off: browser scroll anchoring adjusts scrollTop when
    // content height changes and fires a scroll event that falsifies the
    // pinned-to-bottom tracking — this page manages its own scroll position.
    <div className="bg-background min-h-full flex flex-col -mb-4 md:-mb-8 [overflow-anchor:none]">
      <div ref={messagesColumnRef} className="flex-1 w-full p-6 pb-0 max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold text-text-primary mb-8 animate-fade-in">
          {conversation.title}
        </h2>

        <MessageList
          messages={conversation.messages}
          loading={loading}
          conversationId={id}
          // Title set by Onboarding.js when creating the post-onboarding welcome conversation
          isWelcomeConversation={conversation.title === 'Velkommen til Coord!'}
        />
      </div>

      <StickyInputBar>
        {/* Send-rejection notice (429 / 400 message_too_long). Same warning
            banner styling as the "Merknad" notice above; sits inside the
            sticky card so it is visible right above the input on mobile. */}
        {notice && (
          <div
            role="status"
            className="flex items-start justify-between gap-3 bg-warning-50 border border-warning-200 text-warning-700 px-4 py-3 rounded-lg mb-3 text-sm"
          >
            <span>{notice}</span>
            <button
              type="button"
              onClick={() => setNotice(null)}
              className="shrink-0 -m-3 p-3 text-warning-700 hover:text-warning-900 transition-colors"
              aria-label="Lukk"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>
        )}
        <MessageInput
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSendMessage={handleSendMessage}
          disabled={loading}
        />
      </StickyInputBar>

      {/* Video Player Modal - listens for 'open-video-modal' events */}
      <VideoPlayerModal />
    </div>
  );
};

export default ConversationView;
